import React, { useState, useEffect, useCallback } from 'react';
import { authFetch, API } from '../../api';
import { Plus, Edit2, Trash2, X, ChevronDown, AlertTriangle, Trophy, Calendar, MapPin, Home, Plane } from 'lucide-react';
import { useAuth } from '../../AuthContext';

const POSITION_GROUPS = {
  'QB': 'qb', 'RB': 'rb_fb', 'FB': 'rb_fb', 'WR': 'wr_te', 'TE': 'wr_te',
  'OT': 'ol', 'OG': 'ol', 'C': 'ol', 'RE': 'dl_lb', 'PE': 'dl_lb', 'DT': 'dl_lb', 'DE': 'dl_lb',
  'SAM': 'dl_lb', 'MIKE': 'dl_lb', 'WILL': 'dl_lb', 'LB': 'dl_lb',
  'CB': 'db', 'S': 'db', 'FS': 'db', 'SS': 'db',
  'K': 'k', 'P': 'p', 'LS': 'ls', 'KR': 'kr_pr', 'PR': 'kr_pr'
};

const STATUS_COLORS = {
  starter: 'bg-green-600/20 text-green-300 border-green-600/30',
  backup: 'bg-yellow-600/20 text-yellow-300 border-yellow-600/30',
  injured: 'bg-red-600/20 text-red-300 border-red-600/30'
};

export default function GameStatsTab({ player }) {
  const { user } = useAuth();
  const [gameLogs, setGameLogs] = useState([]);
  const [totals, setTotals] = useState(null);
  const [statDefs, setStatDefs] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [availableSeasons, setAvailableSeasons] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const canEdit = user && !['parent'].includes(user.role);
  const position = player?.position?.toUpperCase() || 'QB';

  const loadData = useCallback(async () => {
    try {
      const seasonParam = selectedSeason ? `?season=${selectedSeason}` : '';
      const [logsRes, totalsRes] = await Promise.all([
        authFetch(`${API}/api/game-logs/${player.id}${seasonParam}`),
        authFetch(`${API}/api/player-stats/${player.id}/totals${seasonParam}`)
      ]);

      if (logsRes.ok) setGameLogs(await logsRes.json());
      if (totalsRes.ok) {
        const t = await totalsRes.json();
        setTotals(t);
        if (!selectedSeason && t.available_seasons?.length > 0) {
          setAvailableSeasons(t.available_seasons);
        }
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [player.id, selectedSeason]);

  const loadStatDefs = useCallback(async (pos) => {
    try {
      const res = await authFetch(`${API}/api/stat-definitions/${pos}`);
      if (res.ok) {
        const data = await res.json();
        setStatDefs(data.stats || []);
      }
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadStatDefs(position); }, [position, loadStatDefs]);

  const handleDelete = async (logId) => {
    if (!window.confirm('Delete this game log and all its stats?')) return;
    try {
      const res = await authFetch(`${API}/api/game-logs/${logId}`, { method: 'DELETE' });
      if (res.ok) loadData();
    } catch (err) { setError('Failed to delete'); }
  };

  const openAdd = () => { setEditingLog(null); setShowModal(true); setError(''); };
  const openEdit = (log) => { setEditingLog(log); setShowModal(true); setError(''); };

  const getDisplayStats = () => {
    if (!totals || !totals.totals) return [];
    const inputDefs = statDefs.filter(d => d.stat_type === 'input');
    const calcDefs = statDefs.filter(d => d.stat_type === 'calculated');
    const items = [];

    for (const def of inputDefs) {
      const val = totals.totals[def.stat_key];
      if (val !== undefined && val !== null) {
        items.push({ key: def.stat_key, name: def.display_name, value: val === Math.floor(val) ? val : val.toFixed(1) });
      }
    }
    for (const def of calcDefs) {
      const val = totals.calculated?.[def.stat_key];
      if (val !== undefined && val !== null) {
        items.push({ key: def.stat_key, name: def.display_name, value: typeof val === 'number' ? (val === Math.floor(val) ? val : val.toFixed(1)) : val, isCalc: true });
      }
    }
    return items;
  };

  return (
    <div className="space-y-6">
      {/* Disclaimer */}
      <div className="bg-yellow-900/15 border border-yellow-700/30 rounded-lg px-4 py-2.5 text-xs text-yellow-300/80 flex items-center gap-2">
        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
        Stats are manually entered by coaching staff and should be independently verified.
      </div>

      {/* Header with season selector and add button */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Trophy className="w-5 h-5 text-[#00D4FF]" /> Game Stats
          </h2>
          {availableSeasons.length > 0 && (
            <select
              value={selectedSeason || ''}
              onChange={(e) => setSelectedSeason(e.target.value ? parseInt(e.target.value) : null)}
              className="px-3 py-1.5 bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
            >
              <option value="">All Seasons</option>
              {availableSeasons.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
        </div>
        {canEdit && (
          <button onClick={openAdd}
            className="flex items-center gap-2 px-3 py-1.5 bg-[#00D4FF] text-[#0A0E27] rounded-lg font-semibold text-sm hover:bg-[#00D4FF]/90">
            <Plus className="w-4 h-4" /> Add Game
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-center text-gray-400 py-12">Loading stats...</div>
      ) : (
        <>
          {/* Season Totals Card */}
          {totals && totals.games_played > 0 && (
            <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-5">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-white font-semibold text-sm">
                  {selectedSeason ? `${selectedSeason} Season Totals` : 'Career Totals'}
                </h3>
                <span className="text-gray-400 text-xs">{totals.games_played} game{totals.games_played !== 1 ? "s" : ""}{totals.injuries > 0 && <span className="text-red-400 ml-2">{totals.injuries} injured</span>}</span>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                {getDisplayStats().map(s => (
                  <div key={s.key} className="text-center">
                    <div className={`text-lg font-bold ${s.isCalc ? 'text-[#00E676]' : 'text-[#00D4FF]'}`}>{s.value}</div>
                    <div className="text-gray-400 text-xs">{s.name}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Game Log Table */}
          {gameLogs.length === 0 ? (
            <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-8 text-center">
              <Trophy className="w-10 h-10 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400 text-sm">No game stats recorded yet.</p>
              {canEdit && <p className="text-gray-500 text-xs mt-1">Click "Add Game" to start tracking.</p>}
            </div>
          ) : (
            <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 overflow-hidden">
              <div className="table-wrap -mx-4 px-4 md:mx-0 md:px-0">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#546E7A]/30 text-gray-400 text-xs uppercase">
                      <th className="px-4 py-3 text-left">Date</th>
                      <th className="px-4 py-3 text-left">Opponent</th>
                      <th className="px-4 py-3 text-center">H/A</th>
                      <th className="px-4 py-3 text-center">Status</th>
                      <th className="px-4 py-3 text-center">Pos</th>
                      {statDefs.filter(d => d.stat_type === 'input').slice(0, 6).map(d => (
                        <th key={d.stat_key} className="px-3 py-3 text-center">{d.display_name}</th>
                      ))}
                      {canEdit && <th className="px-3 py-3 text-center">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {gameLogs.map(log => (
                      <tr key={log.id} className="border-b border-[#546E7A]/15 hover:bg-[#243B55]/20">
                        <td className="px-4 py-3 text-white text-xs">
                          <div className="flex items-center gap-1.5">
                            <Calendar className="w-3 h-3 text-gray-500" />
                            {log.game_date || '—'}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-white text-xs">{log.opponent_name || '—'}</div>
                          {log.opponent_location && <div className="text-gray-500 text-xs">{log.opponent_location}</div>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {log.is_home ?
                            <span className="text-green-400 text-xs flex items-center justify-center gap-1"><Home className="w-3 h-3" />H</span> :
                            <span className="text-blue-400 text-xs flex items-center justify-center gap-1"><Plane className="w-3 h-3" />A</span>
                          }
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-1.5 py-0.5 rounded border text-xs ${STATUS_COLORS[log.player_status] || ''}`}>
                            {log.player_status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center text-gray-300 text-xs">{log.position}</td>
                        {statDefs.filter(d => d.stat_type === 'input').slice(0, 6).map(d => (
                          <td key={d.stat_key} className="px-3 py-3 text-center text-white text-xs">
                            {log.stats[d.stat_key] !== undefined ? (log.stats[d.stat_key] === Math.floor(log.stats[d.stat_key]) ? log.stats[d.stat_key] : log.stats[d.stat_key].toFixed(1)) : '—'}
                          </td>
                        ))}
                        {canEdit && (
                          <td className="px-3 py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <button onClick={() => openEdit(log)} className="p-1 text-gray-400 hover:text-[#00D4FF] rounded"><Edit2 className="w-3.5 h-3.5" /></button>
                              <button onClick={() => handleDelete(log.id)} className="p-1 text-gray-400 hover:text-red-400 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <GameModal
          player={player}
          editingLog={editingLog}
          statDefs={statDefs}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); loadData(); }}
          onStatDefsChange={loadStatDefs}
        />
      )}
    </div>
  );
}


function GameModal({ player, editingLog, statDefs, onClose, onSaved, onStatDefsChange }) {
  const [form, setForm] = useState({
    position: editingLog?.position || player.position?.toUpperCase() || '',
    game_date: editingLog?.game_date || '',
    opponent_name: editingLog?.opponent_name || '',
    opponent_location: editingLog?.opponent_location || '',
    is_home: editingLog?.is_home ?? true,
    player_status: editingLog?.player_status || 'starter',
    season_year: editingLog?.season_year || new Date().getFullYear(),
    notes: editingLog?.notes || '',
  });
  const [stats, setStats] = useState(editingLog?.stats || {});
  const [localDefs, setLocalDefs] = useState(statDefs);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Load stat defs when position changes
  useEffect(() => {
    if (form.position) {
      authFetch(`${API}/api/stat-definitions/${form.position}`)
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data?.stats) setLocalDefs(data.stats);
        });
    }
  }, [form.position]);

  const handleStatChange = (key, value) => {
    setStats({ ...stats, [key]: value === '' ? 0 : parseFloat(value) });
  };

  const computeCalcValue = (def) => {
    if (!def.calc_formula) return '—';
    try {
      let formula = def.calc_formula;
      for (const d of localDefs) {
        if (d.stat_type === 'input') {
          const val = stats[d.stat_key] || 0;
          formula = formula.replace(new RegExp(`\\b${d.stat_key}\\b`, 'g'), val);
        }
      }
      const result = eval(formula);
      return isFinite(result) ? result.toFixed(1) : '—';
    } catch { return '—'; }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    // Filter out zero stats
    const filteredStats = {};
    for (const [k, v] of Object.entries(stats)) {
      if (v !== 0 && v !== null && v !== undefined) filteredStats[k] = v;
    }

    try {
      let res;
      if (editingLog) {
        res = await authFetch(`${API}/api/game-logs/${editingLog.id}`, {
          method: 'PUT',
          body: JSON.stringify({ ...form, stats: filteredStats })
        });
      } else {
        res = await authFetch(`${API}/api/game-logs/${player.id}`, {
          method: 'POST',
          body: JSON.stringify({ ...form, stats: filteredStats })
        });
      }

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to save');
      }
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const positions = Object.keys(POSITION_GROUPS).sort();

  return (
    <div className="sheet-overlay fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
      <div className="sheet bg-[#1A2744] rounded-xl border border-[#546E7A]/30 w-full max-w-2xl max-h-[90vh] overflow-y-auto scroll-y">
        <div className="flex justify-between items-center p-5 border-b border-[#546E7A]/30 sticky top-0 bg-[#1A2744] z-10">
          <h3 className="text-white font-semibold">{editingLog ? 'Edit Game' : 'Add Game'} — {player.name}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && <div className="bg-red-900/30 border border-red-700/50 text-red-300 px-4 py-2 rounded-lg text-sm">{error}</div>}

          {/* Game Info */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Opponent Name</label>
              <input type="text" value={form.opponent_name} onChange={(e) => setForm({...form, opponent_name: e.target.value})}
                className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                placeholder="Rival High School" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Opponent Location</label>
              <input type="text" value={form.opponent_location} onChange={(e) => setForm({...form, opponent_location: e.target.value})}
                className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                placeholder="City, ST" />
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Game Date</label>
              <input type="date" value={form.game_date} onChange={(e) => setForm({...form, game_date: e.target.value})}
                className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00D4FF]" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Season</label>
              <input type="number" value={form.season_year} onChange={(e) => setForm({...form, season_year: parseInt(e.target.value)})}
                className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00D4FF]" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Position</label>
              <select value={form.position} onChange={(e) => setForm({...form, position: e.target.value})}
                className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00D4FF]">
                {positions.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Status</label>
              <select value={form.player_status} onChange={(e) => setForm({...form, player_status: e.target.value})}
                className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00D4FF]">
                <option value="starter">Starter</option>
                <option value="backup">Backup</option>
                <option value="injured">Injured</option>
              </select>
            </div>
          </div>

          {/* Home/Away */}
          <div className="flex items-center gap-4">
            <label className="text-xs font-medium text-gray-400">Game Location:</label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" checked={form.is_home} onChange={() => setForm({...form, is_home: true})}
                className="accent-[#00D4FF]" />
              <span className="text-white text-sm flex items-center gap-1"><Home className="w-3.5 h-3.5 text-green-400" /> Home</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" checked={!form.is_home} onChange={() => setForm({...form, is_home: false})}
                className="accent-[#00D4FF]" />
              <span className="text-white text-sm flex items-center gap-1"><Plane className="w-3.5 h-3.5 text-blue-400" /> Away</span>
            </label>
          </div>

          {/* Stat Fields */}
          <div className="border-t border-[#546E7A]/30 pt-4">
            <h4 className="text-white font-semibold text-sm mb-3">Game Statistics — {form.position}</h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {localDefs.filter(d => d.stat_type === 'input').map(def => (
                <div key={def.stat_key} className={def.is_optional ? 'opacity-70' : ''}>
                  <label className="block text-xs font-medium text-gray-400 mb-1">
                    {def.display_name} {def.is_optional && <span className="text-gray-500">(opt)</span>}
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={stats[def.stat_key] || ''}
                    onChange={(e) => handleStatChange(def.stat_key, e.target.value)}
                    className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
                    placeholder="0"
                  />
                </div>
              ))}
              {localDefs.filter(d => d.stat_type === 'calculated').map(def => (
                <div key={def.stat_key}>
                  <label className="block text-xs font-medium text-[#00E676] mb-1">{def.display_name}</label>
                  <div className="px-3 py-2 bg-[#0A0E27]/50 border border-[#00E676]/20 rounded-lg text-[#00E676] text-sm font-mono">
                    {computeCalcValue(def)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">Game Notes (optional)</label>
            <textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})}
              className="w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/30 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#00D4FF] resize-vertical min-h-[60px]"
              placeholder="Notes about this game..." />
          </div>

          {/* Submit */}
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving}
              className="px-6 py-2.5 bg-[#00D4FF] text-[#0A0E27] rounded-lg font-semibold text-sm hover:bg-[#00D4FF]/90 disabled:opacity-50">
              {saving ? 'Saving...' : editingLog ? 'Update Game' : 'Save Game'}
            </button>
            <button type="button" onClick={onClose}
              className="px-6 py-2.5 bg-gray-700 text-gray-300 rounded-lg font-semibold text-sm hover:bg-gray-600">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}
